// src/App.jsx
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';

// Importa tus componentes principales
import Header from './components/Header/Header';
import ProtectedRoute from './components/ProtectedRoute';

// Importa Paginas públicas
import MaintenancePage from './pages/MaintenancePage/MaintenancePage';
import HowToConnect from './pages/HowToConnect/HowToConnect';
import HeroSection from './pages/HeroSection/HeroSection';
import ShopPage from './pages/ShopPage/ShopPage';
import MasInformacion from './pages/MasInformacion/MasInformacion';

import ComingSoonPage from './pages/ComingSoonPage/ComingSoonPage';

// Importa los componentes de autenticación
import LoginPage from './pages/Auth/LoginPage';
import DiscordCallback from './pages/Auth/DiscordCallback';
import UserDashboard from './pages/UserDashboard/UserDashboard';

// Importa los componentes de administración
import AdminDashboard from './pages/Admin/AdminDashboard/AdminDashboard';
import AdminUsers from './pages/Admin/AdminUsers/AdminUsers';
import AdminRoles from './pages/Admin/AdminRoles/AdminRoles';
import AdminNormativa from './pages/AdminNormativa/AdminNormativa'; // Importar nueva página
import AdminWhitelist from './pages/Admin/AdminWhitelist/AdminWhitelist';
import AdminProfile from './pages/Admin/AdminProfile/AdminProfile';
import AdminLogs from './pages/Admin/AdminLogs/AdminLogs';
import AdminGallery from './pages/Admin/AdminGallery/AdminGallery';
import AdminReviews from './pages/Admin/AdminReviews/AdminReviews';
import AdminChangeRequests from './pages/Admin/AdminChangeRequests/AdminChangeRequests';
import AdminSystemConfig from './pages/Admin/AdminSystemConfig/AdminSystemConfig';
import AdminMaintenance from './pages/Admin/AdminMaintenance/AdminMaintenance';
import AdminAlerts from './pages/Admin/AdminAlerts/AdminAlerts';

// Importa el componente NotFound para rutas no encontradas
import NotFound from './pages/NotFound/NotFound';

// IMPORTA AuthContext Y AuthProvider desde su propio archivo
import { AuthContext, AuthProvider } from './context/AuthContext';

// Se agregó la ruta para el formulario de Whitelist en lugar de ComingSoonPage.
import Whitelist from './pages/Whitelist/Whitelist';
import Normativa from './pages/Normativa/Normativa';

// Importar componentes de notificación
import ToastContainer from './components/ToastContainer/ToastContainer';
import AlertDisplay from './components/AlertDisplay/AlertDisplay';
import { useToast } from './hooks/useToast';




const APP_VERSION = '1.2.1';

const AppContent = ({
  isUnderMaintenance,
  toggleMaintenanceMode,
}) => {
  const { isLoggedIn, user, hasPermission } = useContext(AuthContext);
  const location = useLocation();
  const { toasts, addToast, removeToast } = useToast();

  // Determina si la ruta actual está exenta del modo mantenimiento (ej. /login, /admin/*)
  const isMaintenanceExemptRoute = location.pathname.startsWith('/login') || location.pathname.startsWith('/admin');

  const canBypassMaintenance = isLoggedIn && (user?.role === 'superadmin' || hasPermission('ver_dashboard'));

  // Lógica para ocultar el Header:
  // El header NO se mostrará si:
  // 1. La ruta empieza con /admin.
  // 2. La aplicación está en modo mantenimiento Y la ruta NO es una ruta exenta.
  const showHeader = !location.pathname.startsWith('/admin') &&
    !(isUnderMaintenance && !isMaintenanceExemptRoute && !canBypassMaintenance);

  // Efecto para inicializar AOS
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });
  }, []);

  const showMaintenanceBanner = isUnderMaintenance && canBypassMaintenance;

  return (
    <>
      {/* Banner movido al tope, fuera del main */}
      {showMaintenanceBanner && (
        <div style={{
          position: 'fixed', // Cambiado a fixed para que se quede arriba
          top: 0,
          left: 0,
          width: '100%',
          zIndex: 1100,
          backgroundColor: '#e65100',
          color: 'white',
          padding: '5px',
          textAlign: 'center',
          fontWeight: '500',
          borderBottom: '2px solid #ef6c00',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          height: '35px', // Altura reducida para que sea más fino
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          ⚠️ MODO MANTENIMIENTO ACTIVO: El sitio público está deshabilitado. Solo visible para administradores.
        </div>
      )}

      {/* Si no está cargando, muestra el contenido de la aplicación */}
      <>
        {/* Renderiza el Header SOLO si showHeader es true, desplazado si hay banner */}
        {showHeader && <Header style={showMaintenanceBanner ? { top: '55px' } : {}} />}
        <main className="main-content">
          {/* Lógica para el modo mantenimiento */}
          {isUnderMaintenance && !isMaintenanceExemptRoute && !canBypassMaintenance ? (
            // Si el modo mantenimiento está activo Y la ruta NO está exenta Y el usuario NO puede saltarlo,
            // muestra solo la página de mantenimiento.
            <MaintenancePage />
          ) : (
            // De lo contrario (no hay mantenimiento O la ruta está exenta O es admin),
            // renderiza las rutas normales de la aplicación.
            <>
              <Routes>
                {/* Rutas Públicas */}
                <Route path="/" element={<HeroSection />} />
                <Route path="/como-conectarse" element={<HowToConnect />} />
                <Route path="/whitelist" element={<Whitelist />} />
                <Route path="/normativa" element={<Normativa />} />
                <Route path="/mas-informacion" element={<MasInformacion />} />
                <Route path="/tienda" element={<ShopPage />} />

                {/* Ruta de Login y Callbacks */}
                <Route path="/login" element={<LoginPage isStaff={false} />} />
                <Route path="/login/staff" element={<LoginPage isStaff={true} />} />
                <Route path="/login/discord/callback" element={<DiscordCallback />} />

                {/* Panel de Usuario - accesible por todos (galería pública, resto protegido internamente) */}
                <Route path="/dashboard" element={<UserDashboard />} />

                {/* Rutas de Administración Protegidas */}
                <Route
                  path="/admin"
                  element={<ProtectedRoute requiredPermission="ver_dashboard" />}
                >
                  <Route index element={<AdminDashboard isUnderMaintenance={isUnderMaintenance} toggleMaintenanceMode={toggleMaintenanceMode} />} />
                </Route>
                <Route
                  path="/admin/users"
                  element={<ProtectedRoute requiredPermission="gestionar_usuarios" />}
                >
                  <Route index element={<AdminUsers />} />
                </Route>
                <Route
                  path="/admin/roles"
                  element={<ProtectedRoute requiredPermission="gestionar_roles" />}
                >
                  <Route index element={<AdminRoles />} />
                </Route>
                <Route
                  path="/admin/whitelist"
                  element={<ProtectedRoute requiredPermission="gestionar_whitelist_solicitudes" />}
                >
                  <Route index element={<AdminWhitelist />} />
                </Route>
                <Route
                  path="/admin/change-requests"
                  element={<ProtectedRoute requiredPermission="gestionar_whitelist_solicitudes" />}
                >
                  <Route index element={<AdminChangeRequests />} />
                </Route>
                <Route
                  path="/admin/logs"
                  element={<ProtectedRoute requiredPermission="ver_logs" />}
                >
                  <Route index element={<AdminLogs />} />
                </Route>
                <Route
                  path="/admin/gallery"
                  element={<ProtectedRoute requiredPermission="ver_dashboard" />}
                >
                  <Route index element={<AdminGallery />} />
                </Route>
                <Route
                  path="/admin/reviews"
                  element={<ProtectedRoute requiredPermission="ver_dashboard" />}
                >
                  <Route index element={<AdminReviews />} />
                </Route>
                <Route
                  path="/admin/profile"
                  element={<AdminProfile />}
                />
                <Route
                  path="/admin/normativas"
                  element={<ProtectedRoute requiredPermission="ver_dashboard" />} // Asumimos permiso básico por ahora
                >
                  <Route index element={<AdminNormativa />} />
                </Route>
                <Route
                  path="/admin/system-config"
                  element={<ProtectedRoute requiredPermission="activar_modo_mantenimiento" />}
                >
                  <Route index element={<AdminSystemConfig isUnderMaintenance={isUnderMaintenance} toggleMaintenanceMode={toggleMaintenanceMode} />} />
                </Route>
                <Route
                  path="/admin/maintenance"
                  element={<ProtectedRoute requiredPermission="activar_modo_mantenimiento" />}
                >
                  <Route index element={<AdminMaintenance />} />
                </Route>
                <Route
                  path="/admin/alerts"
                  element={<ProtectedRoute requiredPermission="activar_modo_mantenimiento" />}
                >
                  <Route index element={<AdminAlerts />} />
                </Route>

                {/* Si no está logueado e intenta acceder a una ruta /admin/*, redirige al login */}
                {!isLoggedIn && (
                  <Route path="/admin/*" element={<Navigate to="/login" replace />} />
                )}

                {/* Ruta Catch-all para 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </>
          )}
        </main>
        <AlertDisplay page={location.pathname.startsWith('/admin') ? 'admin' : 'home'} />
        <ToastContainer
          toasts={toasts}
          onRemoveToast={removeToast}
        />
      </>
    </>
  );
}

function App() {
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);

  // Función para obtener el estado del modo mantenimiento desde el servidor
  const fetchMaintenanceStatus = useCallback(async () => {
    console.log('🔍 Obteniendo estado de mantenimiento desde:', 'https://riasbaixaspd.es/api/maintenance');
    try {
      const response = await fetch('https://riasbaixaspd.es/api/maintenance');
      console.log('📥 Estado fetch response:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Estado obtenido:', data);
        setIsUnderMaintenance(data.isUnderMaintenance || false);
      } else {
        console.error('❌ Error fetching maintenance status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching maintenance status:', error);
      // Fallback a localStorage si el servidor no responde
      const storedMaintenance = localStorage.getItem('isUnderMaintenance');
      setIsUnderMaintenance(storedMaintenance ? JSON.parse(storedMaintenance) : false);
    }
  }, []);

  const toggleMaintenanceMode = useCallback(async () => {
    console.log('🔄 toggleMaintenanceMode ejecutado. Estado actual:', isUnderMaintenance);
    try {
      const newMode = !isUnderMaintenance;
      const token = localStorage.getItem('authToken');

      console.log('📡 Enviando petición a:', 'https://riasbaixaspd.es/api/maintenance');
      console.log('🔑 Token:', token ? 'Presente' : 'No encontrado');
      console.log('📝 Nuevo modo:', newMode);

      const response = await fetch('https://riasbaixaspd.es/api/maintenance', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          enabled: newMode
        }),
      });

      console.log('📥 Respuesta status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Respuesta exitosa:', data);
        setIsUnderMaintenance(data.isUnderMaintenance);
        // También guardamos en localStorage como backup
        localStorage.setItem('isUnderMaintenance', JSON.stringify(data.isUnderMaintenance));
      } else {
        const errorText = await response.text();
        console.error('❌ Error updating maintenance mode. Status:', response.status, 'Response:', errorText);
      }
    } catch (error) {
      console.error('Error toggling maintenance mode:', error);
      // Fallback a localStorage si hay error con el servidor
      const newMode = !isUnderMaintenance;
      setIsUnderMaintenance(newMode);
      localStorage.setItem('isUnderMaintenance', JSON.stringify(newMode));
    }
  }, [isUnderMaintenance]);

  // Cargar el estado inicial del modo mantenimiento y verificar versión para cache-busting
  useEffect(() => {
    // Verificar versión para forzar recarga si ha cambiado
    const savedVersion = localStorage.getItem('app_version');
    if (savedVersion !== APP_VERSION) {
      console.log('🔄 Nueva versión detectada, limpiando caché y recargando...');
      localStorage.clear();
      sessionStorage.clear();
      // Eliminar cachés del navegador (Cache Storage) si existen
      if ('caches' in window) {
        caches.keys().then((names) => {
          for (let name of names) {
            caches.delete(name);
          }
        }).catch(err => console.warn('No se pudo limpiar Cache Storage:', err));
      }
      localStorage.setItem('app_version', APP_VERSION);
      window.location.reload(true);
      return;
    }

    fetchMaintenanceStatus();
  }, [fetchMaintenanceStatus]);

  // Eliminando la pantalla de carga inicial
  return (
    <Router>
      <AuthProvider>
        <AppContent
          isUnderMaintenance={isUnderMaintenance}
          toggleMaintenanceMode={toggleMaintenanceMode}
        />
      </AuthProvider>
    </Router>
  );
}

export default App;